import React, { useState } from "react";
import {
  MobileScreenSettingsFaq,
  MobileScreenSettingsPageRelation,
  MobileScreenSettingsServiceRelation,
  MobileScreenSettingsWithRelations,
  MobileScreenSettingsScreenTypes,
  MobileScreenSettingsShortcuts,
} from "@/types/mobile-settings";
import { ReorderableSection } from "@/components/generic/ReorderableSection";
import { ShortcutItem } from "./ShortcutItem";
import { ServiceItem } from "./ServiceItem";
import { FaqItem } from "./FaqItem";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AddShortcutDialog } from "./AddShortcutDialog";
import { AddServiceDialog } from "./AddServiceDialog";
import { AddFaqDialog } from "./AddFaqDialog";
import { AddPageDialog } from "./AddPageDialog";
import { Button } from "@/components/ui/button";
import { MapPinIcon } from "lucide-react";
import type { FAQ } from "@/types/faq";
import type {
  HotelService,
  HotelServiceProviderType,
} from "@/types/hotelService";
import type { CustomPage } from "@/types/custom-page";
import { useTranslation } from "react-i18next";
import { PageItem } from "./PageItem";

interface DashboardSettingsProps {
  settings: MobileScreenSettingsWithRelations;
  onUpdate: (data: Partial<MobileScreenSettingsWithRelations>) => void;
  onSave: () => void;
  screenType: MobileScreenSettingsScreenTypes;
  onScreenTypeChange: (type: MobileScreenSettingsScreenTypes) => void;
  serviceProviderTypes?: HotelServiceProviderType[];
  showServiceShortcut?: boolean;
  hideServiceSections?: boolean;
}

export const DashboardSettings: React.FC<DashboardSettingsProps> = ({
  settings,
  onUpdate,
  onSave,
  serviceProviderTypes,
  showServiceShortcut = true,
  hideServiceSections = false,
}) => {
  const { t } = useTranslation();
  const [isAddShortcutOpen, setIsAddShortcutOpen] = useState(false);
  const [isAddRecommendedOpen, setIsAddRecommendedOpen] = useState(false);
  const [isAddFaqOpen, setIsAddFaqOpen] = useState(false);
  const [isAddFeaturedOpen, setIsAddFeaturedOpen] = useState(false);
  const [isAddCtaOpen, setIsAddCtaOpen] = useState(false);
  const [isAddPageOpen, setIsAddPageOpen] = useState(false);

  const handleReorderShortcuts = (
    newShortcuts: MobileScreenSettingsShortcuts[],
  ) => {
    onUpdate({
      mobileScreenSettingsShortcuts: newShortcuts,
    });
  };

  const handleAddShortcut = (name: string) => {
    const newShortcut = {
      id: `temp-${Date.now()}`,
      name,
      link: name,
      mobileScreenSettingsId: settings.id,
      order: settings.mobileScreenSettingsShortcuts.length,
    };
    onUpdate({
      mobileScreenSettingsShortcuts: [
        ...settings.mobileScreenSettingsShortcuts,
        newShortcut,
      ],
    });
  };

  const handleDeleteShortcut = (item: MobileScreenSettingsShortcuts) => {
    const newShortcuts = settings.mobileScreenSettingsShortcuts.filter(
      (s) => s.id !== item.id,
    );
    onUpdate({
      mobileScreenSettingsShortcuts: newShortcuts,
    });
  };

  const handleReorderRecommended = (
    newServices: MobileScreenSettingsServiceRelation[],
  ) => {
    onUpdate({
      mobileScreenSettingsRecommendedServices: newServices,
    });
  };

  const handleAddRecommended = (service: HotelService) => {
    const newServiceRelation: MobileScreenSettingsServiceRelation = {
      id: `temp-${Date.now()}`,
      serviceId: service.id,
      service,
      order: settings.mobileScreenSettingsRecommendedServices.length,
    };
    onUpdate({
      mobileScreenSettingsRecommendedServices: [
        ...settings.mobileScreenSettingsRecommendedServices,
        newServiceRelation,
      ],
    });
  };

  const handleDeleteRecommended = (
    item: MobileScreenSettingsServiceRelation,
  ) => {
    const newServices = settings.mobileScreenSettingsRecommendedServices.filter(
      (s) => s.id !== item.id,
    );
    onUpdate({
      mobileScreenSettingsRecommendedServices: newServices,
    });
  };

  const handleReorderFaqs = (newFaqs: MobileScreenSettingsFaq[]) => {
    onUpdate({
      mobileScreenSettingsFaqs: newFaqs,
    });
  };

  const handleAddFaq = (faq: FAQ) => {
    const newFaqRelation: MobileScreenSettingsFaq = {
      id: `temp-${Date.now()}`,
      faqId: faq.id,
      faq,
      order: (settings.mobileScreenSettingsFaqs ?? []).length,
    };
    onUpdate({
      mobileScreenSettingsFaqs: [
        ...(settings.mobileScreenSettingsFaqs ?? []),
        newFaqRelation,
      ],
    });
  };

  const handleDeleteFaq = (item: MobileScreenSettingsFaq) => {
    const newFaqs = (settings.mobileScreenSettingsFaqs ?? []).filter(
      (faq) => faq.id !== item.id,
    );
    onUpdate({
      mobileScreenSettingsFaqs: newFaqs,
    });
  };

  const handleReorderFeatured = (
    newActivities: MobileScreenSettingsServiceRelation[],
  ) => {
    onUpdate({
      mobileScreenSettingsFeaturedActivities: newActivities,
    });
  };

  const handleAddFeatured = (service: HotelService) => {
    const newActivityRelation: MobileScreenSettingsServiceRelation = {
      id: `temp-${Date.now()}`,
      serviceId: service.id,
      service,
      order: settings.mobileScreenSettingsFeaturedActivities.length,
    };
    onUpdate({
      mobileScreenSettingsFeaturedActivities: [
        ...settings.mobileScreenSettingsFeaturedActivities,
        newActivityRelation,
      ],
    });
  };

  const handleDeleteFeatured = (item: MobileScreenSettingsServiceRelation) => {
    const newActivities =
      settings.mobileScreenSettingsFeaturedActivities.filter(
        (s) => s.id !== item.id,
      );
    onUpdate({
      mobileScreenSettingsFeaturedActivities: newActivities,
    });
  };

  const handleReorderCta = (
    newServices: MobileScreenSettingsServiceRelation[],
  ) => {
    onUpdate({
      mobileScreenSettingsCtaServices: newServices,
    });
  };

  const handleAddCta = (service: HotelService) => {
    const newServiceRelation: MobileScreenSettingsServiceRelation = {
      id: `temp-${Date.now()}`,
      serviceId: service.id,
      service,
      order: settings.mobileScreenSettingsCtaServices.length,
    };

    onUpdate({
      mobileScreenSettingsCtaServices: [
        ...settings.mobileScreenSettingsCtaServices,
        newServiceRelation,
      ],
    });
  };

  const handleDeleteCta = (item: MobileScreenSettingsServiceRelation) => {
    const newServices = settings.mobileScreenSettingsCtaServices.filter(
      (serviceRelation) => serviceRelation.id !== item.id,
    );

    onUpdate({
      mobileScreenSettingsCtaServices: newServices,
    });
  };

  const handleReorderPages = (newPages: MobileScreenSettingsPageRelation[]) => {
    onUpdate({
      mobileScreenSettingsPages: newPages,
    });
  };

  const handleAddPage = (page: CustomPage) => {
    const newPageRelation: MobileScreenSettingsPageRelation = {
      id: `temp-${Date.now()}`,
      customPageId: page.id,
      customPage: page,
      order: settings.mobileScreenSettingsPages.length,
    };

    onUpdate({
      mobileScreenSettingsPages: [
        ...settings.mobileScreenSettingsPages,
        newPageRelation,
      ],
    });
  };

  const handleDeletePage = (item: MobileScreenSettingsPageRelation) => {
    const newPages = settings.mobileScreenSettingsPages.filter(
      (pageRelation) => pageRelation.id !== item.id,
    );

    onUpdate({
      mobileScreenSettingsPages: newPages,
    });
  };

  return (
    <div className="font-patrick space-y-8 py-6">
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="address-visibility"
            checked={settings.isAddressVisible}
            onCheckedChange={(checked) =>
              onUpdate({
                hotelId: settings.hotelId,
                screenType: settings.screenType,
                isAddressVisible: checked,
              })
            }
          />
          <Label htmlFor="address-visibility" className="text-lg font-bold">
            Show hotel address on screen
          </Label>
        </div>

        <div className="space-y-2">
          <div className="flex cursor-not-allowed justify-between border-b-2 border-b-black p-3 text-zinc-500">
            {settings.hotel?.hotelAddress?.fullAddress}
            <MapPinIcon />
          </div>
        </div>
      </div>

      <ReorderableSection
        title="Shortcuts carousel"
        items={settings.mobileScreenSettingsShortcuts}
        onReorder={handleReorderShortcuts}
        onAdd={() => setIsAddShortcutOpen(true)}
        onDelete={handleDeleteShortcut}
        keyExtractor={(item) => item.id}
        renderItem={(item) => <ShortcutItem shortcut={item} />}
      />

      {!hideServiceSections ? (
        <ReorderableSection
          title={t(
            "mobileSettings.dashboard.recommendedServices",
            "Recommended Services",
          )}
          items={settings.mobileScreenSettingsRecommendedServices}
          onReorder={handleReorderRecommended}
          onAdd={() => setIsAddRecommendedOpen(true)}
          onDelete={handleDeleteRecommended}
          keyExtractor={(item) => item.id}
          renderItem={(item) => <ServiceItem service={item.service} />}
        />
      ) : null}

      <ReorderableSection
        title={t(
          "mobileSettings.dashboard.recommendedFaqs",
          "Recommended FAQs",
        )}
        items={settings.mobileScreenSettingsFaqs ?? []}
        onReorder={handleReorderFaqs}
        onAdd={() => setIsAddFaqOpen(true)}
        onDelete={handleDeleteFaq}
        keyExtractor={(item) => item.id}
        renderItem={(item) => <FaqItem faq={item.faq} />}
      />

      <ReorderableSection
        title={t(
          "mobileSettings.dashboard.featuredActivities",
          "Featured Activities",
        )}
        items={settings.mobileScreenSettingsFeaturedActivities}
        onReorder={handleReorderFeatured}
        onAdd={() => setIsAddFeaturedOpen(true)}
        onDelete={handleDeleteFeatured}
        keyExtractor={(item) => item.id}
        renderItem={(item) => <ServiceItem service={item.service} />}
      />

      {!hideServiceSections ? (
        <ReorderableSection
          title={t("mobileSettings.dashboard.callToAction", "Call to Action")}
          items={settings.mobileScreenSettingsCtaServices}
          onReorder={handleReorderCta}
          onAdd={() => setIsAddCtaOpen(true)}
          onDelete={handleDeleteCta}
          keyExtractor={(item) => item.id}
          renderItem={(item) => <ServiceItem service={item.service} />}
        />
      ) : null}

      <ReorderableSection
        title={t("mobileSettings.dashboard.pages", "Pages")}
        items={settings.mobileScreenSettingsPages}
        onReorder={handleReorderPages}
        onAdd={() => setIsAddPageOpen(true)}
        onDelete={handleDeletePage}
        keyExtractor={(item) => item.id}
        renderItem={(item) => <PageItem page={item.customPage} />}
      />

      <div className="border-t border-zinc-200 pt-4">
        <Button
          onClick={onSave}
          className="h-12 w-full rounded-xl bg-zinc-900 text-lg font-bold text-white"
        >
          Save Changes
        </Button>
      </div>

      <AddShortcutDialog
        open={isAddShortcutOpen}
        onOpenChange={setIsAddShortcutOpen}
        onAdd={handleAddShortcut}
        showServiceShortcut={showServiceShortcut}
      />

      <AddServiceDialog
        open={isAddRecommendedOpen}
        onOpenChange={setIsAddRecommendedOpen}
        onAdd={handleAddRecommended}
        hotelId={settings.hotelId}
        existingIds={settings.mobileScreenSettingsRecommendedServices.map(
          (s) => s.serviceId,
        )}
        title="Add Recommended Service"
        type="recommended"
        providerTypes={serviceProviderTypes}
      />

      <AddFaqDialog
        open={isAddFaqOpen}
        onOpenChange={setIsAddFaqOpen}
        onAdd={handleAddFaq}
        hotelId={settings.hotelId}
        existingIds={(settings.mobileScreenSettingsFaqs ?? []).map(
          (faq) => faq.faqId,
        )}
      />

      <AddServiceDialog
        open={isAddFeaturedOpen}
        onOpenChange={setIsAddFeaturedOpen}
        onAdd={handleAddFeatured}
        hotelId={settings.hotelId}
        existingIds={settings.mobileScreenSettingsFeaturedActivities.map(
          (s) => s.serviceId,
        )}
        title="Add Featured Activity"
        type="featured"
      />

      <AddServiceDialog
        open={isAddCtaOpen}
        onOpenChange={setIsAddCtaOpen}
        onAdd={handleAddCta}
        hotelId={settings.hotelId}
        existingIds={settings.mobileScreenSettingsCtaServices.map(
          (serviceRelation) => serviceRelation.serviceId,
        )}
        title={t("mobileSettings.dashboard.addCallToAction", "Add CTA Service")}
        type="cta"
        providerTypes={serviceProviderTypes}
      />

      <AddPageDialog
        open={isAddPageOpen}
        onOpenChange={setIsAddPageOpen}
        onAdd={handleAddPage}
        existingIds={settings.mobileScreenSettingsPages.map(
          (pageRelation) => pageRelation.customPageId,
        )}
      />
    </div>
  );
};
